import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Ticket, Wallet, Users, Activity, Gift, ExternalLink, Copy, Check, Play, Pause, Dices, Plus, ReceiptText, Clock, Loader2, Trophy, Download, Settings2, QrCode, HandHeart } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav, BasarMetric } from "../components/basar/BasarNav";
import { ShareCardPanel } from "../components/basar/ShareCardPanel";
import { PosterButtons } from "../components/basar/PosterButtons";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { basarStatusMeta, basarUrl, dashboardBasarUrl, nok, shortDateTime, soldPercent, useMyBasar } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

// /dashboard/basar/:id – oversikt: nøkkeltal, status (kladd → open → stengt → trekt), offentleg lenkje og snarvegar
export default function BasarAdminPage() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const { basar, vippsAvailable, loading, error, reload, setBasar } = useMyBasar(id);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null); // "open" | "closed" | "draft"

  useEffect(() => {
    if (error === "notfound") navigate("/dashboard/basar");
  }, [error, navigate]);

  const setStatus = async (status) => {
    setConfirm(null);
    setBusy(status);
    try {
      const { data } = await api.patch(`/my/basar/${id}/status`, { status });
      setBasar(data.basar);
      if (data.warning) toast.warning(data.warning);
      else toast.success(status === "open" ? t("Loddsalget er åpnet") : status === "closed" ? t("Loddsalget er stengt") : t("Basaren er satt tilbake til kladd"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const publicUrl = `${window.location.origin}${basarUrl(id)}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success(t("Lenken er kopiert"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("Kunne ikke kopiere – merk lenken og kopier manuelt"));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/my/basar/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "basar-lodd.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const m = basar?.metrics || {};
  const st = basar ? basarStatusMeta(basar) : null;
  const prizes = basar?.prizes || [];
  const copies = prizes.reduce((s, p) => s + (p.count || 1), 0);
  const drawnCount = (basar?.draws || []).reduce((s, d) => s + (d.winners?.length || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-admin-page">
        <BasarNav basar={basar} />
        {loading || !basar ? (
          <div className="space-y-6" data-testid="basar-loading-state"><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>
        ) : (
          <>
            {/* Varsel: kladd utan premiar / Vipps manglar */}
            {basar.status === "draft" && prizes.length === 0 && (
              <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-empty-state">
                <Gift className="h-4 w-4" />
                <AlertTitle>{t("Legg til minst én premie før basaren åpnes")}</AlertTitle>
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">{t("Premiene vises på basarsiden og trekkes i Right in.")} <Button asChild size="sm" variant="outline" data-testid="basar-admin-add-prizes-link"><Link to={dashboardBasarUrl(id, "premiar")}><Plus className="w-4 h-4 mr-1.5" /> {t("Legg til premier")}</Link></Button></AlertDescription>
              </Alert>
            )}
            {!vippsAvailable && basar.status !== "drawn" && (
              <Alert data-testid="basar-vipps-warning">
                <Wallet className="h-4 w-4" />
                <AlertTitle>{t("Vipps er ikke satt opp")}</AlertTitle>
                <AlertDescription>{t("Publikum kan ikke kjøpe lodd på nett før Vipps er klart. Manuelle salg kan registreres uansett.")} <Link to="/dashboard/betaling" className="underline underline-offset-4 font-medium">{t("Gå til Betaling")}</Link></AlertDescription>
              </Alert>
            )}

            {/* Nøkkeltal */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="basar-metrics">
              <BasarMetric icon={Ticket} label={t("Solgte lodd")} value={`${m.sold ?? 0} / ${m.total ?? basar.total}`} testId="basar-metric-sold" hint={m.held > 0 ? t("{n} reservert i Vipps nå", { n: m.held }) : t("{n} ledige", { n: m.remaining ?? 0 })}>
                <Progress value={soldPercent(m)} className="h-1.5 mt-2 bg-[hsl(var(--warning))]/15 [&>div]:bg-[hsl(var(--warning))]" />
              </BasarMetric>
              <BasarMetric icon={Wallet} label={t("Omsetning")} value={nok(m.revenue || 0)} testId="basar-metric-revenue" hint={t("Vipps {vipps} · manuelt {manual}", { vipps: nok(m.revenue_vipps || 0), manual: nok((m.revenue || 0) - (m.revenue_vipps || 0)) })} />
              <BasarMetric icon={Users} label={t("Kjøpere")} value={m.buyers ?? 0} testId="basar-metric-buyers" hint={m.orders === 1 ? t("{n} salg", { n: 1 }) : t("{n} salg", { n: m.orders ?? 0 })} />
              <BasarMetric icon={Activity} label={t("Status")} value={<Badge className={st.className} data-testid="basar-metric-status-badge">{st.label}</Badge>} testId="basar-metric-status" hint={basar.status === "drawn" ? t("Publisert {date}", { date: formatDateTime(basar.draw_published_at) }) : basar.sale_end ? t("Salget slutter {date}", { date: shortDateTime(basar.sale_end) }) : basar.sale_start ? t("Salget starter {date}.", { date: shortDateTime(basar.sale_start) }) : t("Ingen salgsperiode satt")} />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
              {/* Status og handlingar */}
              <Card className="border-border lg:col-span-7 min-w-0">
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div>
                    <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Status og salg")}</h2>
                    <p className="text-sm text-muted-foreground">
                      {basar.status === "draft" && t("Basaren er en kladd og vises ikke offentlig ennå.")}
                      {basar.status === "open" && t("Loddsalget er åpent. Publikum kan kjøpe lodd på basarsiden, og dere kan registrere salg ved døren.")}
                      {basar.status === "closed" && t("Salget er stengt. Du kan fortsatt registrere manuelle salg, og trekke vinnerne når dere er klare.")}
                      {basar.status === "drawn" && t("Trekningen er publisert. Vinnerlisten ligger på basarsiden.")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="basar-status-actions">
                    {basar.status === "draft" && (
                      <Button onClick={() => setConfirm("open")} disabled={busy !== "" || prizes.length === 0} title={prizes.length === 0 ? t("Legg til minst én premie før basaren åpnes") : undefined} data-testid="basar-open-button">
                        {busy === "open" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />} {t("Åpne loddsalget")}
                      </Button>
                    )}
                    {basar.status === "open" && (
                      <>
                        <Button variant="outline" onClick={() => setConfirm("closed")} disabled={busy !== ""} data-testid="basar-close-button">{busy === "closed" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Pause className="w-4 h-4 mr-1.5" />} {t("Steng salget")}</Button>
                        {(m.sold ?? 0) === 0 && <Button variant="ghost" onClick={() => setConfirm("draft")} disabled={busy !== ""} data-testid="basar-draft-button">{t("Tilbake til kladd")}</Button>}
                      </>
                    )}
                    {basar.status === "closed" && (
                      <>
                        <Button asChild data-testid="basar-go-draw-button"><Link to={dashboardBasarUrl(id, "trekning")}><Dices className="w-4 h-4 mr-1.5" /> {t("Gå til trekning")}</Link></Button>
                        <Button variant="outline" onClick={() => setConfirm("open")} disabled={busy !== ""} data-testid="basar-reopen-button">{busy === "open" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />} {t("Åpne salget igjen")}</Button>
                      </>
                    )}
                    {basar.status === "drawn" && (
                      <Button asChild variant="outline" data-testid="basar-view-winners-button"><a href={basarUrl(id)} target="_blank" rel="noreferrer"><Trophy className="w-4 h-4 mr-1.5" /> {t("Se vinnerlisten")}</a></Button>
                    )}
                  </div>
                  {m.pending > 0 && (
                    <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5" data-testid="basar-pending-note"><Clock className="w-4 h-4 text-[hsl(var(--info))]" /> {t("{n} betaling(er) venter på Vipps. Ubetalte reservasjoner frigis automatisk etter 15 minutter.", { n: m.pending })}</p>
                  )}
                  <div className="border-t border-border pt-4 flex flex-wrap gap-2">
                    <Button asChild variant="secondary" size="sm" data-testid="basar-admin-manual-sale-link"><Link to={`${dashboardBasarUrl(id, "sal")}?ny=1`}><Plus className="w-4 h-4 mr-1.5" /> {t("Registrer salg ved døren")}</Link></Button>
                    <Button asChild variant="secondary" size="sm" data-testid="basar-admin-orders-link"><Link to={dashboardBasarUrl(id, "sal")}><ReceiptText className="w-4 h-4 mr-1.5" /> {t("Alle salg")}</Link></Button>
                    <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="basar-admin-export-button"><Download className="w-4 h-4 mr-1.5" /> {t("Eksporter CSV")}</Button>
                    <Button asChild variant="secondary" size="sm" data-testid="basar-admin-check-link"><Link to={dashboardBasarUrl(id, "kontroll")}><QrCode className="w-4 h-4 mr-1.5" /> {t("Utlevering (skann kvittering)")}</Link></Button>
                    <Button asChild variant="secondary" size="sm" data-testid="basar-admin-gifts-link"><Link to={dashboardBasarUrl(id, "gaver")}><HandHeart className="w-4 h-4 mr-1.5" /> {t("Gaver og gaveregister")}{m.gifts_to_collect > 0 ? ` (${m.gifts_to_collect})` : ""}</Link></Button>
                    <PosterButtons basarId={id} />
                    <Button asChild variant="secondary" size="sm" data-testid="basar-admin-setup-link"><Link to={dashboardBasarUrl(id, "oppsett")}><Settings2 className="w-4 h-4 mr-1.5" /> {t("Oppsett")}</Link></Button>
                  </div>
                </CardContent>
              </Card>

              <div className="lg:col-span-5 space-y-4 min-w-0">
                {/* Offentleg lenkje */}
                <Card className="border-border" data-testid="basar-public-link-card">
                  <CardContent className="p-5 space-y-3">
                    <p className="font-heading font-semibold">{t("Lenke til basarsiden")}</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 min-w-0 truncate rounded-md bg-muted px-3 py-2 text-xs" data-testid="basar-public-url">{publicUrl}</code>
                      <Button variant="outline" size="icon" onClick={copyLink} aria-label={t("Kopier lenke")} data-testid="basar-copy-link-button">{copied ? <Check className="w-4 h-4 text-[hsl(var(--success))]" /> : <Copy className="w-4 h-4" />}</Button>
                      <Button asChild variant="outline" size="icon" aria-label={t("Se offentlig side")} data-testid="basar-open-public-button"><a href={basarUrl(id)} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>
                    </div>
                    <p className="text-xs text-muted-foreground">{basar.status === "draft" ? t("Siden blir synlig når loddsalget åpnes. Basaren vises også på arrangørsiden og i den felles oversikten.") : t("Del lenken på Facebook, i gruppechatten eller på plakaten. Basaren vises også på arrangørsiden og i den felles oversikten /basar.")}</p>
                  </CardContent>
                </Card>
                {/* Delingskort (Phase 76) */}
                <ShareCardPanel basar={basar} />
                {/* Premiar */}
                <Card className="border-border" data-testid="basar-prizes-summary">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-heading font-semibold">{t("Premier")}</p>
                        <p className="text-sm text-muted-foreground tabular-nums">{prizes.length === 1 ? t("{n} premie", { n: 1 }) : t("{n} premier", { n: prizes.length })}{copies !== prizes.length ? ` · ${t("{n} eksemplarer", { n: copies })}` : ""} · {t("verdi {amount}", { amount: nok(m.prize_value || 0) })}</p>
                      </div>
                      <Button asChild variant="outline" size="sm" data-testid="basar-admin-prizes-link"><Link to={dashboardBasarUrl(id, "premiar")}><Gift className="w-4 h-4 mr-1.5" /> {basar.draw_published_at ? t("Se premier") : t("Rediger")}</Link></Button>
                    </div>
                    {prizes.length > 0 && (
                      <ul className="text-sm space-y-1">
                        {prizes.slice(0, 5).map((p, i) => <li key={p.id} className="flex justify-between gap-3"><span className="truncate">{i + 1}. {p.name}{p.count > 1 ? ` × ${p.count}` : ""}</span><span className="text-muted-foreground tabular-nums shrink-0">{p.value ? nok(p.value) : ""}</span></li>)}
                        {prizes.length > 5 && <li className="text-xs text-muted-foreground">{t("+ {n} til", { n: prizes.length - 5 })}</li>}
                      </ul>
                    )}
                    {basar.status !== "drawn" && drawnCount > 0 && <p className="text-xs text-[hsl(var(--warning))]" data-testid="basar-admin-draw-progress">{t("Trekning påbegynt: {drawn} av {total} trukket (ikke publisert)", { drawn: drawnCount, total: copies })}</p>}
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="basar-status-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "open" ? t("Åpne loddsalget?") : confirm === "closed" ? t("Stenge loddsalget?") : t("Sette basaren tilbake til kladd?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "open" && (vippsAvailable ? t("Basarsiden blir offentlig, og publikum kan kjøpe lodd med Vipps.") : t("Basarsiden blir offentlig. Vipps er ikke satt opp, så publikum kan ikke kjøpe på nett ennå – men dere kan registrere salg manuelt."))}
              {confirm === "closed" && t("Publikum kan ikke lenger kjøpe lodd på nett. Manuelle salg kan fortsatt registreres, og du kan åpne igjen senere.")}
              {confirm === "draft" && t("Basaren skjules fra offentligheten igjen.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="basar-status-confirm-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => setStatus(confirm)} data-testid="basar-status-confirm-ok">{confirm === "open" ? t("Åpne") : confirm === "closed" ? t("Steng") : t("Ja")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
